(() => {
  'use strict';

  const OPT_OUT_KEY = 'shipping_protection_optout';
  const CART_SECTION = 'berlinde-cart';

  const rootUrl = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const cartUrl = (path) => `${rootUrl()}${path}`.replace(/([^:]\/)\/+/g, '$1');

  const cartSectionId = () => {
    const host = document.querySelector('[data-bl-cart-host]');
    return (host && host.dataset.blCartSectionId) || CART_SECTION;
  };

  const setBusy = (busy) => {
    window.__shippingProtectionBusy = busy;
    document.querySelectorAll('shipping-protection').forEach((component) => {
      if (busy) component.setAttribute('aria-busy', 'true');
      else component.removeAttribute('aria-busy');
    });
  };

  const updateVisibleCount = () => {
    const source = document.querySelector('[data-bl-cart-count-source]');
    if (!source) return;
    const value = source.textContent.trim();
    document.querySelectorAll('[data-bl-cart-count]').forEach((count) => {
      count.textContent = value;
    });
  };

  const preserveAndSyncUpsell = (currentHost, freshHost) => {
    let freshUpsell = freshHost.querySelector('.bl-cart-upsell');
    const currentUpsell = currentHost.querySelector('.bl-cart-upsell');

    if (!freshUpsell && currentUpsell) {
      const freshBody = freshHost.querySelector('.bl-cart__body');
      if (freshBody) {
        freshUpsell = currentUpsell.cloneNode(true);
        freshBody.appendChild(freshUpsell);
      }
    }

    if (!freshUpsell) return;

    const cartProductIds = new Set(
      [...freshHost.querySelectorAll('[data-bl-cart-product-id]')].map((row) =>
        row.dataset.blCartProductId
      )
    );
    let visibleCards = 0;

    freshUpsell.querySelectorAll('[data-bl-upsell-product-id]').forEach((card) => {
      const inCart = cartProductIds.has(card.dataset.blUpsellProductId);
      card.hidden = inCart;
      card.dataset.blUpsellInCart = inCart ? 'true' : 'false';
      if (!inCart) visibleCards += 1;
    });
    freshUpsell.dataset.blUpsellVisibleCount = String(visibleCards);
    freshUpsell.hidden = visibleCards === 0;
  };

  const applyCartSection = (sections, sectionId) => {
    if (!sections) return false;
    const sectionHtml = sections[sectionId] || sections[CART_SECTION];
    if (!sectionHtml) return false;

    const target = document.querySelector('[data-bl-cart-host]');
    if (!target) return false;

    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const source = parsed.querySelector('[data-bl-cart-host]');
    if (!source) return false;

    const drawer = target.querySelector('[data-bl-cart]');
    const wasOpen = drawer && drawer.dataset.open === 'true';
    const protectionHadFocus = Boolean(
      document.activeElement && document.activeElement.closest('shipping-protection')
    );

    preserveAndSyncUpsell(target, source);
    target.innerHTML = source.innerHTML;
    if (window.BerlindeUpsell) window.BerlindeUpsell.init(target);

    if (wasOpen) {
      const freshDrawer = target.querySelector('[data-bl-cart]');
      if (freshDrawer) {
        freshDrawer.dataset.open = 'true';
        freshDrawer.setAttribute('aria-hidden', 'false');
      }
      const scrim = document.querySelector('[data-bl-scrim]');
      if (scrim) scrim.dataset.open = 'true';
    }

    updateVisibleCount();

    if (protectionHadFocus) {
      const freshInput = target.querySelector('.shipping-protection__input');
      if (freshInput) freshInput.focus();
    }

    return true;
  };

  const getCart = async () => {
    const response = await fetch(cartUrl('cart.js'), {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(`Cart read failed: ${response.status}`);
    return response.json();
  };

  const changeCart = async (endpoint, payload, reloadAfterChange = false) => {
    if (window.__shippingProtectionBusy) return;
    setBusy(true);
    const sectionId = cartSectionId();

    try {
      const response = await fetch(cartUrl(endpoint), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          ...payload,
          sections: [sectionId],
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) throw new Error(`Cart change failed: ${response.status}`);
      const data = await response.json();
      if (reloadAfterChange) {
        window.location.reload();
        return;
      }
      const refreshed = applyCartSection(data.sections, sectionId);

      document.dispatchEvent(
        new CustomEvent('shipping-protection:changed', { detail: { cart: data } })
      );

      if (!refreshed) window.location.reload();
    } catch (error) {
      console.error('[shipping-protection]', error);
      window.location.reload();
    } finally {
      setBusy(false);
    }
  };

  const findProtectionLine = (cart, variantId) =>
    cart.items.find((item) => item.id === variantId || item.variant_id === variantId);

  const addProtection = async (variantId, reloadAfterChange = false) => {
    const cart = await getCart();
    const line = findProtectionLine(cart, variantId);

    if (line && line.quantity === 1) return;
    if (line) {
      return changeCart('cart/change.js', { id: line.key, quantity: 1 }, reloadAfterChange);
    }

    return changeCart('cart/add.js', {
      items: [{ id: variantId, quantity: 1 }]
    }, reloadAfterChange);
  };

  const removeProtection = async (variantId, reloadAfterChange = false) => {
    const cart = await getCart();
    const line = findProtectionLine(cart, variantId);
    if (!line) return;
    return changeCart('cart/change.js', { id: line.key, quantity: 0 }, reloadAfterChange);
  };

  const storage = {
    get() {
      try {
        return window.localStorage.getItem(OPT_OUT_KEY);
      } catch (_) {
        return null;
      }
    },
    set(value) {
      try {
        if (value === null) window.localStorage.removeItem(OPT_OUT_KEY);
        else window.localStorage.setItem(OPT_OUT_KEY, value);
      } catch (_) {
        // Storage can be unavailable in private or restricted browser contexts.
      }
    }
  };

  class ShippingProtection extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';
      this.variantId = Number.parseInt(this.dataset.variantId, 10);
      this.defaultOn = this.dataset.defaultOn === 'true';
      this.reloadAfterChange = this.dataset.reloadAfterChange === 'true';
      this.input = this.querySelector('.shipping-protection__input');
      if (!this.variantId || !this.input) return;

      this.input.addEventListener('change', () => this.onChange());
      this.sync();
    }

    async sync() {
      if (window.__shippingProtectionBusy) return;

      try {
        const cart = await getCart();
        const line = findProtectionLine(cart, this.variantId);
        const normalItems = cart.items.filter(
          (item) => item.id !== this.variantId && item.variant_id !== this.variantId
        );

        if (line && normalItems.length === 0) {
          await changeCart(
            'cart/change.js',
            { id: line.key, quantity: 0 },
            this.reloadAfterChange
          );
          return;
        }

        if (line && line.quantity !== 1) {
          this.input.checked = true;
          await changeCart(
            'cart/change.js',
            { id: line.key, quantity: 1 },
            this.reloadAfterChange
          );
          return;
        }

        this.input.checked = Boolean(line);
        const optedOut = storage.get() === '1';
        if (!line && normalItems.length > 0 && this.defaultOn && !optedOut) {
          await addProtection(this.variantId, this.reloadAfterChange);
        }
      } catch (error) {
        console.error('[shipping-protection]', error);
      }
    }

    async onChange() {
      try {
        if (this.input.checked) {
          storage.set(null);
          await addProtection(this.variantId, this.reloadAfterChange);
        } else {
          storage.set('1');
          await removeProtection(this.variantId, this.reloadAfterChange);
        }
      } catch (error) {
        console.error('[shipping-protection]', error);
        window.location.reload();
      }
    }
  }

  if (!customElements.get('shipping-protection')) {
    customElements.define('shipping-protection', ShippingProtection);
  }
})();
